import { max, ScaleContinuousNumeric, scaleLinear } from 'd3'
import { useMemo } from 'react'

import { buildLiquidityBuckets, LIQUIDITY_BUCKET_COUNT } from './chartUtils'
import { ChartEntry } from './types'

const RANGE_INDICATOR_WIDTH = 16
const BAR_GAP = 1

export function LiquidityBars({
  series,
  yScale,
  current,
  brushDomain,
  innerWidth,
  innerHeight,
  railWidth,
  lowerColor,
  upperColor,
}: {
  series: ChartEntry[]
  yScale: ScaleContinuousNumeric<number, number>
  current: number
  brushDomain?: [number, number]
  innerWidth: number
  innerHeight: number
  railWidth: number
  lowerColor: string
  upperColor: string
}) {
  const buckets = useMemo(() => buildLiquidityBuckets(series, yScale.domain() as [number, number]), [series, yScale])
  const maximumLiquidity = max(buckets, (bucket) => bucket.activeLiquidity) ?? 1
  const maximumBarWidth = Math.max(3, railWidth - RANGE_INDICATOR_WIDTH - 10)
  const widthScale = scaleLinear().domain([0, maximumLiquidity]).range([3, maximumBarWidth])
  const bucketHeight = innerHeight / LIQUIDITY_BUCKET_COUNT
  const [rangeMinimum, rangeMaximum] = brushDomain
    ? brushDomain[0] <= brushDomain[1]
      ? brushDomain
      : [brushDomain[1], brushDomain[0]]
    : [undefined, undefined]

  return (
    <g aria-label="Active liquidity">
      {buckets.map((bucket) => {
        const width = widthScale(bucket.activeLiquidity)
        const inRange =
          rangeMinimum !== undefined &&
          rangeMaximum !== undefined &&
          bucket.price >= rangeMinimum &&
          bucket.price <= rangeMaximum

        return (
          <rect
            key={bucket.index}
            x={innerWidth - RANGE_INDICATOR_WIDTH - width - 1}
            y={bucket.index * bucketHeight + BAR_GAP / 2}
            width={width}
            height={Math.max(2, bucketHeight - BAR_GAP)}
            rx="1.5"
            fill={bucket.price >= current ? upperColor : lowerColor}
            opacity={inRange ? 0.92 : 0.32}
          />
        )
      })}
    </g>
  )
}
